import { PlainTextMath } from '@/components/axi/PlainTextMath'
import { IAiConversationMessageAssistant } from '@/types/firebase'
import React, { FC, useEffect, useLayoutEffect, useState } from 'react'
import { HTMLMotionProps, motion } from 'framer-motion'
import {
  messageAnimationPropsDiv,
  messageAnimationPropsDivContainer,
} from '@/utils/ai-assistant'
import 'katex/dist/katex.min.css'
import TikZRenderer from '@/components/ui/common/TikZRenderer'
import { cleanTopicName } from '@/utils/topic-name-cleaner'
import { CommonMarkdownLatexComponent } from '@/components/ui/common/lessons'

interface Props {
  message: IAiConversationMessageAssistant
  useAnimation?: boolean
  stopAnimation?: () => void
  promptButtons: React.ReactNode | null
  useMainTextBlockAnimation?: boolean
  isLastMessage?: boolean
}

export const AiAssistantTutorMessageContent: FC<Props> = ({
  message: { content, conversationId, id },
  useAnimation,
  stopAnimation,
  promptButtons,
  useMainTextBlockAnimation = true,
  isLastMessage,
}) => {
  const [showExample, setShowExample] = useState(false)
  const [showCommonMistake, setShowCommonMistake] = useState(false)
  const [showTip, setShowTip] = useState(false)
  const [showFollowUp, setShowFollowUp] = useState(false)
  const [showPromptBtns, setShowPromptBtns] = useState(false)

  // show all blocks if animation is disabled
  useLayoutEffect(() => {
    if (!useAnimation) {
      setShowExample(true)
      setShowCommonMistake(true)
      setShowTip(true)
      setShowFollowUp(true)
      setShowPromptBtns(true)

      return
    }
  }, [useAnimation])

  // handle case when should be animated parts except main text block
  useEffect(() => {
    if (useAnimation && !useMainTextBlockAnimation) {
      if (content.example) {
        setShowExample(true)

        return
      }

      if (!content.followUpQuestion && stopAnimation) {
        stopAnimation()
        return
      }

      setShowExample(true)
      setShowCommonMistake(true)
      setShowTip(true)
      setShowFollowUp(true)
      setShowPromptBtns(true)
    }
  }, [
    useAnimation,
    useMainTextBlockAnimation,
    content.example,
    content.followUpQuestion,
    stopAnimation,
  ])

  const divAnimationProps: HTMLMotionProps<'div'> = useAnimation
    ? messageAnimationPropsDiv
    : {
        // variants: {
        //   hidden: { opacity: 1, y: 0 },
        //   visible: {
        //     opacity: 1,
        //     y: 0,
        //   },
        // },
      }

  const divContainerAnimationProps: HTMLMotionProps<'div'> = useAnimation
    ? messageAnimationPropsDivContainer
    : {}

  return (
    <motion.div
      {...divContainerAnimationProps}
      className="w-full flex flex-col gap-6"
    >
      <div className="w-full flex flex-col gap-2">
        {/* Topic */}
        {content.topic && (
          <p className="text-lg text-v8-ink font-semibold">
            {cleanTopicName(content.topic)}
          </p>
        )}
        {/* Main Text */}
        {useMainTextBlockAnimation ? (
          <motion.div
            {...divAnimationProps}
            onAnimationComplete={
              // triggers only if animation was applied
              () => {
                if (content.example) {
                  setShowExample(true)

                  return
                }

                if (!content.followUpQuestion && stopAnimation) {
                  stopAnimation()
                  return
                }

                setShowExample(true)
                setShowCommonMistake(true)
                setShowTip(true)
                setShowFollowUp(true)
                setShowPromptBtns(true)
              }
            }
            className={`w-full flex flex-col gap-4`}
          >
            <div className="whitespace-pre-line">
              {content.plainText ? (
                <PlainTextMath text={content.text} />
              ) : (
                <CommonMarkdownLatexComponent text={content.text} />
              )}
            </div>

            {content.visualRepresentation && (
              <TikZRenderer
                tikzCode={content.visualRepresentation}
                id={`${conversationId}-${id}`}
                shouldTriggerTikZRenderInitially={isLastMessage}
              />
            )}
          </motion.div>
        ) : (
          <div className={`w-full flex flex-col gap-4`}>
            <div className="whitespace-pre-line">
              {content.plainText ? (
                <PlainTextMath text={content.text} />
              ) : (
                <CommonMarkdownLatexComponent text={content.text} />
              )}
            </div>

            {/* Visual Representation */}
            {content.visualRepresentation && (
              <TikZRenderer
                tikzCode={content.visualRepresentation}
                id={`${conversationId}-${id}`}
                shouldTriggerTikZRenderInitially={isLastMessage}
              />
            )}
          </div>
        )}
      </div>

      {/* Example Block */}
      {showExample && content.example ? (
        <>
          <div className="w-full p-2 flex flex-col gap-2 bg-v8-paper rounded-md">
            {/* Example */}
            <p className="text-v8-ink font-semibold">Example</p>
            <motion.div
              className="whitespace-pre-line"
              {...divAnimationProps}
              onAnimationComplete={
                // triggers only if animation was applied
                () => {
                  if (content.commonMistake || content.tip) {
                    setShowCommonMistake(true)
                    setShowTip(true)

                    return
                  }

                  if (!content.followUpQuestion && stopAnimation) {
                    stopAnimation()
                    return
                  }

                  setShowExample(true)
                  setShowCommonMistake(true)
                  setShowTip(true)
                  setShowFollowUp(true)
                  setShowPromptBtns(true)
                }
              }
            >
              <CommonMarkdownLatexComponent text={content.example} />
            </motion.div>

            <div className="w-full flex flex-col gap-2">
              {/* Common Mistake */}
              {showCommonMistake && content.commonMistake && (
                <motion.div
                  {...divAnimationProps}
                  onAnimationComplete={
                    // triggers only if animation was applied
                    () => {
                      if (!content.followUpQuestion && stopAnimation) {
                        stopAnimation()
                        return
                      }

                      setShowFollowUp(true)
                    }
                  }
                  className="w-full p-2 flex flex-col gap-2 bg-red-200 border border-red-400 rounded-md whitespace-pre-line"
                >
                  <p className="text-v8-ink font-semibold">Common Mistake</p>
                  <CommonMarkdownLatexComponent text={content.commonMistake} />
                </motion.div>
              )}
              {/* Tip */}
              {showTip && content.tip && (
                <motion.div
                  {...divAnimationProps}
                  onAnimationComplete={
                    // triggers only if animation was applied
                    () => {
                      if (!content.followUpQuestion && stopAnimation) {
                        stopAnimation()
                        return
                      }

                      setShowFollowUp(true)
                    }
                  }
                  className="w-full p-2 flex flex-col gap-2 bg-v8-blue/10 border border-v8-line rounded-xl whitespace-pre-line"
                >
                  <p className="text-v8-ink font-semibold">Tip</p>
                  <CommonMarkdownLatexComponent text={content.tip} />
                </motion.div>
              )}
            </div>
          </div>

          {/* Follow-Up Question */}
          {showFollowUp && content.followUpQuestion && (
            <motion.div
              {...divAnimationProps}
              className="w-full p-2 flex flex-col gap-2 whitespace-pre-line"
              onAnimationComplete={
                // triggers only if animation was applied
                () => {
                  if (!promptButtons && stopAnimation) {
                    stopAnimation()
                  }

                  setShowPromptBtns(true)
                }
              }
            >
              <CommonMarkdownLatexComponent text={content.followUpQuestion} />
            </motion.div>
          )}
        </>
      ) : (
        <>
          {/* Follow-Up Question */}
          {showFollowUp && content.followUpQuestion && (
            <motion.div
              {...divAnimationProps}
              className="w-full flex flex-col gap-2 whitespace-pre-line"
              onAnimationComplete={
                // triggers only if animation was applied
                () => {
                  if (!promptButtons && stopAnimation) {
                    stopAnimation()
                  }

                  setShowPromptBtns(true)
                }
              }
            >
              <CommonMarkdownLatexComponent text={content.followUpQuestion} />
            </motion.div>
          )}
        </>
      )}

      {/* Prompt Buttons */}
      {showPromptBtns && (
        <motion.div
          {...divAnimationProps}
          onAnimationComplete={
            // triggers only if animation was applied
            () => {
              stopAnimation && stopAnimation()
            }
          }
          className="w-full"
        >
          {promptButtons}
        </motion.div>
      )}
    </motion.div>
  )
}
